import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import { assertArrayFields } from "@utils/apiPayload";
import { fetchDownload, type DownloadedFile } from "@utils/fileDownload";
import { buildQueryString } from "@utils/queryString";
import type { GetMeInvoicesQuery, PaginatedMeInvoices } from "@models/invoice";
import { isFetchingMeInvoicesAtom } from "@/store/invoiceAtoms";

const INVOICES_ENDPOINT = "/invoices";
const ME_INVOICES_ENDPOINT = `${INVOICES_ENDPOINT}/me`;

export const useInvoiceApi = () => {
    const { callApi } = useApi();
    const setIsFetchingMeInvoices = useSetAtom(isFetchingMeInvoicesAtom);

    // GET /invoices/{invoiceId}/pdf (#213, PR 270). The endpoint needs the
    // bearer token, so the bytes come back through callApi as a Blob and are
    // saved client-side — never linked directly. `fileName` is the server's
    // Content-Disposition name when it sent one (`<InvoiceNumber>.pdf`, or
    // `-VOID.pdf` for a voided invoice; the API exposes the header through
    // CORS); the caller falls back to a name it can build itself.
    // "contracted": the download renders its contracted failures INLINE next
    // to the button — 403 (not entitled) and both 404s (no such invoice /
    // "Invoice PDF is no longer available"), all ObjectResult(string) bodies
    // on the API — so those reject as the ApiError without a toast; everything
    // else keeps useApi's toast and rejects as DownloadError. A 200 that is not
    // a PDF would otherwise be saved as <number>.pdf and reported as success.
    // No loading atom: the only consumer is a single button whose in-flight
    // state is local, and the two pages that mount it never co-render.
    const downloadInvoicePdf = useCallback(
        (invoiceId: string): Promise<DownloadedFile> =>
            fetchDownload(callApi, `${INVOICES_ENDPOINT}/${invoiceId}/pdf`, {
                failures: "contracted",
                acceptedTypes: ["application/pdf"],
            }),
        [callApi],
    );

    // GET /invoices/me (#215): the caller's own order invoices plus their
    // organization's membership invoices — the API scopes it. Lists go out as
    // repeated keys; an empty filter sends no key, which the API reads as all.
    // Failures keep useApi's toast and reject; the Billing History page renders
    // its own failed state from the rejection.
    const getMeInvoices = useCallback(
        async (query: GetMeInvoicesQuery): Promise<PaginatedMeInvoices> => {
            setIsFetchingMeInvoices(true);

            try {
                const page = await callApi<PaginatedMeInvoices>(
                    `${ME_INVOICES_ENDPOINT}?${buildQueryString(query)}`,
                );

                // A 2xx that is not the page shape (an empty 200, a proxy's
                // HTML) would render as neither a list nor an empty state;
                // the throw makes it the page's failed state instead.
                assertArrayFields(ME_INVOICES_ENDPOINT, page, ["items"]);

                return page;
            } finally {
                setIsFetchingMeInvoices(false);
            }
        },
        [callApi, setIsFetchingMeInvoices],
    );

    return { downloadInvoicePdf, getMeInvoices };
};
